import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
  useUpdateBoardStatus,
  useReorderBoardStatuses,
  useDeleteBoardStatus,
} from '@/features/boards/public';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useTranslation } from 'react-i18next';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { serializeFilters } from '../../utils/filter-serializer';
import { computeAfterTaskId } from '../../utils/reorder';
import { useTaskBoard } from '../../hooks/useTaskBoard';
import { useTaskFilters } from '../../hooks/useTaskViewStore';
import { useOpenTask } from '../../hooks/useOpenTask';
import { TASK_KEYS } from '../../hooks/queryKeys';
import type { TaskGroupPage } from '../../hooks/useTaskGroupInfinite';
import type { ProjectStatus, Task } from '../../types';
import type { StatusType } from '@/features/boards/public';
import { KanbanColumn } from './KanbanColumn';
import { AddStatusButton } from './AddStatusButton';

const TASK_STATUS_TO_API: Record<string, StatusType> = {
  todo: 'Todo',
  'in-progress': 'InProgress',
  done: 'Done',
};

const TYPE_ORDER: Record<string, number> = {
  todo: 0,
  'in-progress': 1,
  done: 2,
};

function sortByZone(statuses: ProjectStatus[]): ProjectStatus[] {
  return [...statuses].sort((a, b) => {
    const typeDiff = (TYPE_ORDER[a.type] ?? 0) - (TYPE_ORDER[b.type] ?? 0);
    return typeDiff !== 0 ? typeDiff : a.order - b.order;
  });
}

interface KanbanBoardProps {
  projectId: string;
  boardId: string;
}

export function KanbanBoard({ projectId, boardId }: KanbanBoardProps) {
  const { t } = useTranslation('tasks');
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);
  const queryClient = useQueryClient();
  const { filters } = useTaskFilters(boardId);
  const serializedFilters = serializeFilters(filters);
  const { statuses, isLoading, moveTask, reorderTask, createTask, isColumnFullyLoaded } =
    useTaskBoard(projectId, boardId, serializedFilters, filters.search, undefined);

  const updateStatus = useUpdateBoardStatus(boardId);
  const reorderStatuses = useReorderBoardStatuses(boardId);
  const deleteStatus = useDeleteBoardStatus(boardId);
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();
  const openTask = useOpenTask();

  function getColumnTasks(statusId: string): Task[] {
    const key = TASK_KEYS.groupPage(
      orgId ?? '',
      projectId,
      'status',
      statusId,
      boardId,
      serializedFilters,
      filters.search,
      undefined,
    );
    const data = queryClient.getQueryData<InfiniteData<TaskGroupPage>>(key);
    return data?.pages.flatMap((p) => p.items) ?? [];
  }

  async function handleDeleteStatus(status: ProjectStatus) {
    const result = await confirm({
      title: t('boardView.column.delete'),
      description: t('boardView.column.deleteDescription'),
      variant: 'destructive',
    });
    if (!result.confirmed) return;
    try {
      await deleteStatus.mutateAsync(status.id);
    } catch {
      // global handler shows error toast
    }
  }

  useEffect(() => {
    return monitorForElements({
      onDrop({ source, location }) {
        const targets = location.current.dropTargets;
        if (targets.length === 0) return;

        const sourceData = source.data as {
          type?: string;
          taskId?: string;
          statusId?: string;
          columnStatusType?: string;
        };

        if (sourceData.type === 'column' && sourceData.statusId) {
          // Column reorder: first drop target with statusId is the target column
          const columnTarget = targets.find(
            (t) =>
              (t.data as { statusId?: string; taskId?: string }).statusId &&
              !(t.data as { taskId?: string }).taskId,
          );
          const targetData = columnTarget?.data as { statusId?: string } | undefined;
          if (!targetData?.statusId) return;

          const draggedId = sourceData.statusId;
          const targetId = targetData.statusId;
          if (draggedId === targetId) return;

          const draggedStatus = statuses.find((s) => s.id === draggedId);
          const targetStatus = statuses.find((s) => s.id === targetId);
          if (!draggedStatus || !targetStatus) return;

          const targetApiType = TASK_STATUS_TO_API[targetStatus.type] ?? 'Todo';

          if (draggedStatus.type !== targetStatus.type) {
            updateStatus.mutate({ statusId: draggedId, data: { type: targetApiType } });
          }

          const reordered = sortByZone(statuses)
            .filter((s) => s.id !== draggedId)
            .flatMap((s) => (s.id === targetId ? [s, draggedStatus] : [s]))
            .filter((s) => s.id != null)
            .map((s) => s.id!);

          reorderStatuses.mutate({ orderedIds: reordered });
          return;
        }

        // Task drop: check if dropped on a task card (targets[0]) or directly on a column
        const firstTarget = targets[0].data as { statusId?: string; taskId?: string };
        const { taskId: sourceTaskId, statusId: sourceStatusId } = sourceData;

        if (!sourceTaskId || !sourceStatusId) return;

        if (firstTarget.taskId && firstTarget.statusId) {
          // Dropped on a task card
          const targetTaskId = firstTarget.taskId;
          const targetStatusId = firstTarget.statusId;
          const columnTasks = getColumnTasks(targetStatusId);
          const targetIndex = columnTasks.findIndex((t) => t.id === targetTaskId);
          if (targetIndex === -1) return;
          if (!isColumnFullyLoaded(sourceStatusId) || !isColumnFullyLoaded(targetStatusId)) {
            console.warn('Kanban drop ignored: column not fully loaded');
            return;
          }

          if (sourceStatusId === targetStatusId) {
            reorderTask(sourceTaskId, targetStatusId, targetIndex);
          } else {
            const afterTaskId = computeAfterTaskId(columnTasks, targetIndex);
            moveTask(sourceTaskId, targetStatusId, afterTaskId);
          }
        } else if (firstTarget.statusId) {
          // Dropped on column (empty area) — append to end
          const targetStatusId = firstTarget.statusId;
          if (!targetStatusId) return;
          if (!isColumnFullyLoaded(sourceStatusId) || !isColumnFullyLoaded(targetStatusId)) {
            console.warn('Kanban drop ignored: column not fully loaded');
            return;
          }

          if (sourceStatusId === targetStatusId) {
            const columnTasks = getColumnTasks(targetStatusId);
            reorderTask(sourceTaskId, targetStatusId, columnTasks.length - 1);
          } else {
            const targetTasks = getColumnTasks(targetStatusId);
            const afterTaskId = computeAfterTaskId(targetTasks, targetTasks.length);
            moveTask(sourceTaskId, targetStatusId, afterTaskId);
          }
        }
      },
    });
  }, [statuses, moveTask, reorderTask, updateStatus, reorderStatuses, orgId, projectId, boardId]);

  if (isLoading) return null;

  const sortedStatuses = sortByZone(statuses);

  return (
    <>
      <div className="flex h-full gap-3 overflow-x-auto p-1 pb-4" data-testid="tasks-board-view">
        {sortedStatuses.map((status) => (
          <KanbanColumn
            key={status.id}
            projectId={projectId}
            boardId={boardId}
            filters={filters}
            status={status}
            onCreateTask={(title) => createTask(title, status.id)}
            onTaskClick={(task) => openTask(task.id)}
            onDelete={handleDeleteStatus}
          />
        ))}

        <div className="flex shrink-0 items-start pt-1">
          <AddStatusButton boardId={boardId} />
        </div>
      </div>

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(v) => !v && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
}
